/**
 * Skill description length cap, shared by every surface that creates or edits a
 * skill description (create form, detail-pane editor, zip upload).
 *
 * The backend gate counts Unicode code points (Go's `utf8.RuneCountInString`),
 * so we count code points here too — not UTF-16 units (`String.length`), which
 * would let a 512-"char" string with multibyte characters (em dashes, curly
 * quotes, emoji) sail past the client check and then be rejected by the API.
 */
export const MAX_SKILL_DESCRIPTION_LENGTH = 512;

/** Code-point length, matching the backend's rune-count gate. */
export function skillDescriptionLength(description: string): number {
  return [...description].length;
}

/** Truncate to a code-point count so the result never exceeds the API gate. */
export function truncateSkillDescription(
  description: string,
  max: number = MAX_SKILL_DESCRIPTION_LENGTH,
): string {
  const points = [...description];
  return points.length > max ? points.slice(0, max).join("") : description;
}

const ELLIPSIS = "…";

/**
 * Truncate for display/import so the result doesn't end mid-word: cut at the
 * last whitespace boundary and append an ellipsis. The ellipsis is counted
 * against `max`, so the result still fits the backend gate. Falls back to a
 * hard code-point cut for a single oversized token that has no whitespace.
 */
export function truncateSkillDescriptionAtWord(
  description: string,
  max: number = MAX_SKILL_DESCRIPTION_LENGTH,
): string {
  const points = [...description];
  if (points.length <= max) return description;

  const budget = Math.max(1, max - [...ELLIPSIS].length);
  const hardCut = points.slice(0, budget).join("");

  // Drop the trailing partial word; fall back to the hard cut if that leaves
  // nothing (e.g. one long token with no spaces).
  const atWord = hardCut.replace(/\s+\S*$/u, "");
  const base = atWord.length > 0 ? atWord : hardCut;

  // Strip trailing whitespace/punctuation so we don't get "word, …".
  return base.replace(/[\s.,;:!?–—-]+$/u, "") + ELLIPSIS;
}
